/**
 * Store construction, free of module-level side effects.
 *
 * `redux.ts` owns the singleton store, its persistor and `window.bbkStore`.
 * Tests that need a store of their own — for example to seed storage before
 * rehydration — import from here so they do not construct that singleton.
 */
import { configureStore } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {
  PERSIST_KEY,
  PERSIST_THROTTLE_MS,
} from '@bbkAdminRedux/persist-config';
import { reducersMiddlewares } from '@bbkAdminRedux/reducers-middlewares';
import { rootReducer } from '@bbkAdminRedux/root-reducer';
import { appPersistSlice } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { authPersistedSlice } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { datepickerPersistSlice } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';

const DEBUGGER = new URLSearchParams(window.location.search).has('debugger');

export const persistConfig = {
  key: PERSIST_KEY,
  // The session metadata (`type`) has to survive a reload: it is all the
  // frontend knows about the session, since the credential itself is an
  // unreadable HttpOnly cookie. Without it a fresh page load cannot tell a
  // returning session from a first visit.
  //
  // The per-tab tenant record is deliberately absent: it has its own nested
  // persistor backed by `sessionStorage` (see `root-reducer.ts`).
  whitelist: [
    appPersistSlice.name,
    datepickerPersistSlice.name,
    authPersistedSlice.name,
  ],
  storage,
  throttle: PERSIST_THROTTLE_MS,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const makeStore = () =>
  configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false }).concat(
        ...reducersMiddlewares,
        ...(DEBUGGER ? [createLogger()] : [])
      ),
    devTools: process.env.NODE_ENV !== 'production',
  });
